/**
 * PageShell — every Command Centre page's frame (page standard, Design's
 * sketch, Nino approved 12 Oct; the "One colour" look).
 *
 * One header: the page's icon in a soft blue tile (the menu's icon for
 * this page, found from the address), the title (24 px, sentence case),
 * one line saying what the page is for, and the buttons on the right (at
 * most one blue). On a phone the buttons go full width under the line.
 * The header never moves between "has items", "empty" and "can't load".
 */
import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { navItemForPath } from '@/lib/nav';
import { TOOL } from '@/lib/tool';

/** The icon tile's colour: blue in the Command Centre, amber in the Class Creator (the split, 27 Oct). */
const TILE = TOOL === 'class-creator' ? { bg: 'bg-[#F5B544]/[0.12]', fg: 'text-[#F5B544]' } : { bg: 'bg-[#8b93ff]/[0.12]', fg: 'text-[#8b93ff]' };

export interface Breadcrumb {
  label: string;
  href?: string;
}

interface PageShellProps {
  title: string;
  subtitle?: ReactNode;
  breadcrumbs?: Breadcrumb[];
  actions?: ReactNode;
  /** The title's icon; by default the menu's icon for this page. */
  icon?: LucideIcon;
  children: ReactNode;
  className?: string;
}

export default function PageShell({ title, subtitle, breadcrumbs, actions, icon, children, className }: PageShellProps) {
  const { pathname } = useLocation();
  const Icon = icon ?? navItemForPath(pathname)?.item.icon;
  return (
    <div className={cn('space-y-6', className)}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb" className="flex items-center text-[13px] text-muted-foreground">
          {breadcrumbs.map((crumb, i) => (
            <span key={i} className="flex items-center">
              {i > 0 && <ChevronRight className="mx-1 h-3 w-3" />}
              {crumb.href
                ? <Link to={crumb.href} className="text-[#8b93ff] hover:underline">{crumb.label}</Link>
                : <span className="text-foreground">{crumb.label}</span>}
            </span>
          ))}
        </nav>
      )}

      <header className="flex min-h-[58px] items-start justify-between gap-5 max-md:min-h-0 max-md:flex-col max-md:gap-3">
        <div className="flex min-w-0 items-start gap-3.5">
          {Icon && (
            <span className={cn('flex h-11 w-11 flex-none items-center justify-center rounded-xl max-md:h-10 max-md:w-10', TILE.bg)} aria-hidden>
              <Icon className={cn('h-[22px] w-[22px]', TILE.fg)} />
            </span>
          )}
          <div className="min-w-0">
            <h1 className="text-2xl font-bold leading-8 tracking-[-0.015em] text-foreground max-md:text-[22px] max-md:leading-7">{title}</h1>
            {subtitle && <p className="mt-0.5 text-[13.5px] text-muted-foreground">{subtitle}</p>}
          </div>
        </div>
        {actions && <div className="flex flex-none items-center gap-2 max-md:w-full max-md:[&>*]:flex-1 max-md:[&>*]:justify-center">{actions}</div>}
      </header>

      {children}
    </div>
  );
}
